// Shared per-control metadata for field components: the deterministic id, the
// aria wiring (described-by + invalid), disabled state, and the store mutators
// pulled from context. Keeps each field component focused on its own markup.

import type { DynamicFormField, ValidationError } from '../types'
import { describedByFor, fieldIdFor, labelIdFor } from '../ids'
import {
  isFieldDisabled,
  useFieldContext,
  type ClassNameOverrides,
  type DynamicFormMode,
} from '../rendererContext'

export interface ControlMeta {
  id: string
  /** `aria-labelledby` target for grouped controls without a native <label>. */
  labelId: string | undefined
  describedBy: string | undefined
  ariaInvalid: true | undefined
  /** `aria-required` for the control when the field is required (else undefined). */
  ariaRequired: true | undefined
  disabled: boolean
  hasErrors: boolean
  mode: DynamicFormMode
  classNames: ClassNameOverrides
  setFieldValue(path: string, value: unknown): void
  notifyBlur(path: string): void
}

export function useControlMeta(
  field: DynamicFormField,
  path: string,
  errors: ValidationError[],
): ControlMeta {
  const { mode, classNames, setFieldValue, notifyBlur } = useFieldContext()
  const hasErrors = errors.length > 0
  return {
    id: fieldIdFor(path),
    labelId: field.label ? labelIdFor(path) : undefined,
    describedBy: describedByFor(field, path, hasErrors),
    ariaInvalid: hasErrors || undefined,
    ariaRequired: field.required ? true : undefined,
    disabled: isFieldDisabled(field, mode),
    hasErrors,
    mode,
    classNames,
    setFieldValue,
    notifyBlur,
  }
}

/** Bootstrap control class with the is-invalid modifier when errored. */
export function controlClass(base: string, override: string | undefined, hasErrors: boolean): string {
  return `${override ?? base}${hasErrors ? ' is-invalid' : ''}`
}
